import { Badge, Button, Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { useNavigate } from 'react-router';
import type { Notification } from 'src/notifications/types/Notification';
import { dashboardRow } from './dashboardRows';

const MAX_TILE_ROWS = 3;

export default function DashboardTile({
  label,
  notifications,
  clientNameOf,
  onViewAll,
}: {
  label: string;
  notifications: Notification[];
  clientNameOf: (clientId: string | null) => string | null;
  onViewAll: () => void;
}) {
  const navigate = useNavigate();
  const rows = notifications
    .map(notification => ({ notification, descriptor: dashboardRow(notification.payload) }))
    .filter(row => row.descriptor !== null)
    .slice(0, MAX_TILE_ROWS);

  return (
    <Paper withBorder radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center" wrap="nowrap">
          <Text fw={600} size="sm">
            {label}
          </Text>
          <Badge color="brand" variant="light" size="lg" radius="sm">
            {notifications.length}
          </Badge>
        </Group>
        <Stack gap={4}>
          {rows.map(({ notification, descriptor }) => {
            const name =
              clientNameOf(notification.payload.client_id ?? null) ??
              descriptor?.fallback ??
              'No linked client';
            return (
              <UnstyledButton
                key={notification.id}
                onClick={() => descriptor && navigate(descriptor.to)}
                px="xs"
                py={6}
                style={{ borderRadius: 6 }}
                bg="gray.0"
              >
                <Group justify="space-between" wrap="nowrap" gap="xs">
                  <Text size="sm" truncate>
                    {name}
                  </Text>
                  {descriptor?.detail && (
                    <Text size="xs" c="gray.6" style={{ whiteSpace: 'nowrap' }}>
                      {descriptor.detail}
                    </Text>
                  )}
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
        <Button variant="text" size="xs" onClick={onViewAll} style={{ alignSelf: 'flex-start' }}>
          {notifications.length > MAX_TILE_ROWS
            ? `View all ${notifications.length}`
            : 'View in tray'}
        </Button>
      </Stack>
    </Paper>
  );
}
